"use client";

import { c as _c } from "react/compiler-runtime";
import type { SelectableIdentityOption } from "@/components/utils/input/profile-search/getSelectableIdentity";
import { ApiWaveParticipationIdentitySubmissionWhoCanBeSubmitted } from "@/generated/models/ApiWaveParticipationIdentitySubmissionWhoCanBeSubmitted";
import { ImageScale, getScaledImageUri } from "@/helpers/image.helpers";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Image from "next/image";
interface CreateDropIdentityFieldProps {
  readonly mode: ApiWaveParticipationIdentitySubmissionWhoCanBeSubmitted;
  readonly selectedIdentity: SelectableIdentityOption | null;
  readonly selfIdentity: SelectableIdentityOption | null;
  readonly disabled: boolean;
  readonly errorMessage: string | null;
  readonly onOpenPicker: () => void;
  readonly onClosePanel?: (() => void) | undefined;
}
function IdentityAvatar(t0) {
  const $ = _c(9);
  const {
    identity
  } = t0;
  if (!identity.avatarUrl) {
    let t1;
    if ($[0] !== identity.label) {
      t1 = identity.label.slice(0, 1);
      $[0] = identity.label;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <div className="tw-flex tw-size-10 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-white/5 tw-bg-iron-800 tw-text-sm tw-font-semibold tw-uppercase tw-text-iron-100">{t1}</div>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t1;
  if ($[4] !== identity.avatarUrl) {
    t1 = getScaledImageUri(identity.avatarUrl, ImageScale.W_AUTO_H_50);
    $[4] = identity.avatarUrl;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const t2 = `${identity.label} avatar`;
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = <div className="tw-relative tw-size-10 tw-flex-shrink-0 tw-overflow-hidden tw-rounded-xl tw-bg-iron-800 tw-ring-1 tw-ring-inset tw-ring-white/10"><Image src={t1} alt={t2} fill={true} unoptimized={true} className="tw-object-cover" sizes="40px" /></div>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
function IdentityUnavailable(t0) {
  const $ = _c(6);
  const {
    errorMessage,
    disabled
  } = t0;
  const t1 = `tw-rounded-xl tw-border tw-border-solid tw-bg-black/40 tw-px-4 tw-py-3 tw-shadow-inner ${errorMessage ? "tw-border-error/70" : "tw-border-white/5"} ${disabled ? "tw-opacity-60" : ""}`;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <p className="tw-mb-0 tw-text-sm tw-font-semibold tw-tracking-tight tw-text-iron-50">Identity unavailable</p>;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const t3 = errorMessage ?? "We couldn't determine your identity for this submission.";
  let t4;
  if ($[1] !== t3) {
    t4 = <p className="tw-mb-0 tw-mt-1 tw-text-xs tw-leading-5 tw-text-iron-400">{t3}</p>;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== t1 || $[4] !== t4) {
    t5 = <div className={t1}>{t2}{t4}</div>;
    $[3] = t1;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
}
function IdentitySelectionCard(t0) {
  const $ = _c(21);
  const {
    identity,
    disabled,
    clickable,
    onOpenPicker
  } = t0;
  let t1;
  if ($[0] !== identity) {
    t1 = <IdentityAvatar identity={identity} />;
    $[0] = identity;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== identity.label) {
    t2 = <p className="tw-mb-0 tw-truncate tw-text-sm tw-font-bold tw-leading-tight tw-text-iron-50">{identity.label}</p>;
    $[2] = identity.label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = identity.secondaryLabel ?? "Selected identity for this drop.";
  let t4;
  if ($[4] !== t3) {
    t4 = <p className="tw-mb-0 tw-mt-1 tw-truncate tw-text-xs tw-font-medium tw-text-iron-500">{t3}</p>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t2 || $[7] !== t4) {
    t5 = <div className="tw-min-w-0 tw-flex-1">{t2}{t4}</div>;
    $[6] = t2;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t1 || $[10] !== t5) {
    t6 = <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-3">{t1}{t5}</div>;
    $[9] = t1;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const content = t6;
  let interactionClassName = "";
  if (disabled) {
    interactionClassName = "tw-cursor-not-allowed tw-opacity-60";
  } else {
    if (clickable) {
      interactionClassName = "tw-cursor-pointer desktop-hover:hover:tw-border-white/10 desktop-hover:hover:tw-bg-iron-950/80";
    }
  }
  const className = `tw-group tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-4 tw-rounded-xl tw-border tw-border-solid tw-border-white/5 tw-bg-black/40 tw-p-3 tw-pr-4 tw-text-left tw-shadow-inner tw-transition-colors tw-duration-200 ${interactionClassName}`;
  if (!clickable) {
    let t7;
    if ($[12] !== className || $[13] !== content) {
      t7 = <div className={className}>{content}</div>;
      $[12] = className;
      $[13] = content;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    return t7;
  }
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className="desktop-hover:group-hover:tw-text-primary-200 tw-flex-shrink-0 tw-whitespace-nowrap tw-pl-4 tw-text-xs tw-font-semibold tw-text-primary-300 tw-transition-colors">Change identity</span>;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== className || $[17] !== content || $[18] !== disabled || $[19] !== onOpenPicker) {
    t8 = <button type="button" onClick={onOpenPicker} disabled={disabled} className={className}>{content}{t7}</button>;
    $[16] = className;
    $[17] = content;
    $[18] = disabled;
    $[19] = onOpenPicker;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}
export default function CreateDropIdentityField(t0) {
  const $ = _c(23);
  const {
    mode,
    selectedIdentity,
    selfIdentity,
    disabled,
    errorMessage,
    onOpenPicker,
    onClosePanel
  } = t0;
  const isOnlyMyself = mode === ApiWaveParticipationIdentitySubmissionWhoCanBeSubmitted.OnlyMyself;
  const identity = isOnlyMyself ? selfIdentity : selectedIdentity;
  const showInlineError = !!errorMessage && !(isOnlyMyself && selfIdentity === null);
  if (!isOnlyMyself && !selectedIdentity) {
    return null;
  }
  const t1 = `tw-relative tw-w-full tw-rounded-2xl tw-border tw-border-solid tw-bg-iron-900/80 tw-p-4 tw-shadow-lg ${errorMessage ? "tw-border-error/70" : "tw-border-white/5"} ${disabled ? "tw-opacity-60" : ""}`;
  let t2;
  if ($[0] !== disabled || $[1] !== onClosePanel) {
    t2 = onClosePanel && <button type="button" onClick={onClosePanel} disabled={disabled} className="tw-absolute tw-right-4 tw-top-4 tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-white/5 tw-p-0 tw-text-iron-400 tw-transition-colors disabled:tw-cursor-not-allowed desktop-hover:hover:tw-bg-white/10 desktop-hover:hover:tw-text-white" aria-label="Close identity selection" title="Close identity selection"><XMarkIcon className="tw-h-4 tw-w-4" aria-hidden="true" /></button>;
    $[0] = disabled;
    $[1] = onClosePanel;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = `tw-flex tw-flex-col tw-gap-3 ${onClosePanel ? "tw-pr-10" : ""}`;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <h3 className="tw-mb-0 tw-text-sm tw-font-semibold tw-tracking-tight tw-text-iron-50">Identity to nominate</h3>;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== disabled || $[5] !== errorMessage || $[6] !== identity || $[7] !== isOnlyMyself || $[8] !== onOpenPicker) {
    t5 = identity ? <><IdentitySelectionCard identity={identity} disabled={disabled} clickable={!isOnlyMyself} onOpenPicker={onOpenPicker} /><p className="tw-mb-0 tw-text-xs tw-leading-5 tw-text-iron-400">Add content below to submit this drop.</p></> : <IdentityUnavailable errorMessage={errorMessage} disabled={disabled} />;
    $[4] = disabled;
    $[5] = errorMessage;
    $[6] = identity;
    $[7] = isOnlyMyself;
    $[8] = onOpenPicker;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t3 || $[11] !== t5) {
    t6 = <div className={t3}>{t4}{t5}</div>;
    $[10] = t3;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t1 || $[14] !== t2 || $[15] !== t6) {
    t7 = <div className={t1}>{t2}{t6}</div>;
    $[13] = t1;
    $[14] = t2;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== errorMessage || $[18] !== showInlineError) {
    t8 = showInlineError && <p role="alert" className="tw-mb-0 tw-mt-2 tw-text-xs tw-font-medium tw-text-error">{errorMessage}</p>;
    $[17] = errorMessage;
    $[18] = showInlineError;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t7 || $[21] !== t8) {
    t9 = <div className="tw-mb-3">{t7}{t8}</div>;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
}